/**
 * As regras de LEITURA do rastreamento — separadas da tela de propósito.
 *
 * Elas vivem aqui, e não em `veiculo-page.tsx`, por duas razões que apontam
 * para o mesmo lugar: `src/pages/**` não pode exportar nada (o `react-refresh`
 * exige isso), e regra que não pode ser exportada não pode ser testada. Cada
 * função abaixo carrega uma decisão de leitura que já custou caro:
 *
 *   - `defasagemDaLeitura` existe porque a primeira consulta que funcionou
 *     contra a frota real devolveu uma posição de 23 DIAS atrás. "Registrada em
 *     23/08/2026 01:45" é uma data de aparência inofensiva: quem bate o olho lê
 *     como se fosse de agora;
 *   - `porDia` mantém um grupo para o item SEM data em vez de descartá-lo —
 *     "a 3S mandou algo que não consigo datar" é informação, não lixo;
 *   - `resumoDasOcorrencias` conta POR TIPO porque "12 ocorrências" não diz
 *     nada, e "11 de cerca, 1 de velocidade" diz que o carro está rodando fora
 *     da área prevista em vez de correr.
 */

import { formatarData, SEM_VALOR } from '@locaveri/shared'

// ---------------------------------------------------------------------------
// Posição: quão velha é a leitura
// ---------------------------------------------------------------------------

/**
 * A partir de quando a leitura merece aviso, em minutos.
 *
 * Duas horas é bem mais que o ciclo de sincronização (5 min por padrão): abaixo
 * disso, atraso é operação normal — equipamento sem sinal em garagem, ciclo que
 * acabou de passar. Acima, o rastreador está calado, e é isso que a tela diz.
 */
export const LEITURA_DEFASADA_EM_MINUTOS = 120

/** Há quanto tempo a leitura chegou, em palavras. `null` = não dá para datar. */
export function defasagemDaLeitura(iso: string, agora: Date = new Date()): string | null {
  const instante = new Date(iso).getTime()
  if (Number.isNaN(instante)) {
    return null
  }

  const minutos = Math.floor((agora.getTime() - instante) / 60_000)
  if (minutos < 0) {
    // Relógio do equipamento adiantado. Inventar "em 3 minutos" seria pior que
    // calar — a leitura existe, só não dá para datar a diferença.
    return null
  }
  if (minutos < 1) {
    return 'agora há pouco'
  }
  if (minutos < 60) {
    return `há ${String(minutos)} min`
  }

  const horas = Math.floor(minutos / 60)
  if (horas < 24) {
    return horas === 1 ? 'há 1 h' : `há ${String(horas)} h`
  }

  const dias = Math.floor(horas / 24)
  return dias === 1 ? 'há 1 dia' : `há ${String(dias)} dias`
}

export function leituraEstaDefasada(iso: string, agora: Date = new Date()): boolean {
  const instante = new Date(iso).getTime()
  if (Number.isNaN(instante)) {
    // Data ilegível não é leitura velha — é leitura sem data. Avisar "defasada"
    // aqui seria afirmar algo que não se sabe.
    return false
  }
  return agora.getTime() - instante > LEITURA_DEFASADA_EM_MINUTOS * 60_000
}

// ---------------------------------------------------------------------------
// Ocorrências: cerca virtual e alertas
// ---------------------------------------------------------------------------

export const TIPOS_DE_OCORRENCIA = [
  'ALERTA_VELOCIDADE',
  'ALERTA_SENSOR',
  'CERCA_ALVO',
  'CERCA_LOGRADOURO',
  'CERCA_POLIGONAL',
] as const

export type TipoDeOcorrencia = (typeof TIPOS_DE_OCORRENCIA)[number]

/**
 * O vocabulário da 3S traduzido para o de quem opera frota blindada.
 *
 * "CercaAlvo" e "CercaPoligonal" são nomes do cadastro deles; na operação o que
 * importa é a FORMA da área — um raio em volta de um ponto, ou um polígono
 * desenhado no mapa —, porque é isso que explica por que o alarme disparou.
 */
export const ROTULO_DA_OCORRENCIA: Record<TipoDeOcorrencia, string> = {
  ALERTA_VELOCIDADE: 'Excesso de velocidade',
  ALERTA_SENSOR: 'Sensor do veículo',
  CERCA_ALVO: 'Cerca por raio',
  CERCA_LOGRADOURO: 'Cerca de via',
  CERCA_POLIGONAL: 'Cerca por área',
}

export interface OcorrenciaParaLeitura {
  readonly tipo: TipoDeOcorrencia
  readonly quando: string | null
  readonly descricao: string
  readonly nivelRisco: number | null
}

export const SEM_DATA = 'Sem data informada pela 3S'

/** Agrupa por dia local, preservando a ordem que veio (mais recente primeiro). */
export function porDia<T extends { readonly quando: string | null }>(
  itens: readonly T[],
): { dia: string; itens: T[] }[] {
  const grupos: { dia: string; itens: T[] }[] = []
  for (const item of itens) {
    const dia = item.quando === null ? SEM_DATA : formatarData(item.quando)
    const ultimo = grupos.at(-1)
    if (ultimo?.dia === dia) {
      ultimo.itens.push(item)
    } else {
      grupos.push({ dia, itens: [item] })
    }
  }
  return grupos
}

/** Só a hora, no fuso da operação — o dia já está no cabeçalho do grupo. */
export function horaDe(iso: string | null): string {
  if (iso === null) {
    return SEM_VALOR
  }
  const data = new Date(iso)
  return Number.isNaN(data.getTime())
    ? SEM_VALOR
    : data.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      })
}

/** O resumo em uma frase: quantas, de quê — do tipo mais frequente ao menos. */
export function resumoDasOcorrencias(itens: readonly OcorrenciaParaLeitura[]): string {
  const porTipo = new Map<TipoDeOcorrencia, number>()
  for (const item of itens) {
    porTipo.set(item.tipo, (porTipo.get(item.tipo) ?? 0) + 1)
  }
  return [...porTipo.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([tipo, quantas]) => `${String(quantas)} de ${ROTULO_DA_OCORRENCIA[tipo].toLowerCase()}`)
    .join(', ')
}
